import React from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Text,
  Textarea,
} from "@chakra-ui/react";

type RejectReasonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  leastRef: React.RefObject<HTMLButtonElement>;
  rejectReason: string;
  setRejectReason: React.Dispatch<React.SetStateAction<string>>;
  onConfirm: () => Promise<void> | void;
  // New optional props to make this reusable for Approve as well
  title?: string;
  description?: string;
  submitLabel?: string;
  requireReason?: boolean;
  quickTags?: string[];
};

const defaultRejectTags = ["Invalid Documents", "Duplicate Entry", "Spam"];

export const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  onClose,
  leastRef,
  rejectReason,
  setRejectReason,
  onConfirm,
  title = "Reason for Rejection",
  description = "Please specify the reason for rejecting this user.",
  submitLabel = "Submit Rejection",
  requireReason = true,
  quickTags,
}) => {
  const isDisabled = requireReason ? !rejectReason.trim() : false;
  const tags = quickTags && quickTags.length ? quickTags : defaultRejectTags;
  const maxLen = 200;
  const overLimit = rejectReason.length > maxLen;

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={leastRef as any} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent maxW={{ base: "90%", md: "800px", xl: "900px" }} mx="auto">
          <AlertDialogHeader fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text mb={3} fontSize={{ base: "sm", md: "md" }}>
              {description}
            </Text>

            <Textarea
              placeholder="Type your reason here — maximum 200 characters"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value.slice(0, maxLen))}
              minH={{ base: "150px", xl: "200px" }}
              border="1px solid #D5DBE3"
              borderRadius={8}
              p={3}
              fontSize="14px"
              resize="vertical"
              maxLength={maxLen}
            />
            <Flex justify="space-between" mt={2} align="center">
              <Text fontSize="xs" color={overLimit ? "red.500" : "gray.500"}>
                {rejectReason.length}/{maxLen} characters
              </Text>
            </Flex>

            <Flex gap={2} mt={3} wrap="wrap">
              {tags.map((tag) => (
                <Button
                  key={tag}
                  size="sm"
                  variant="outline"
                  borderColor="#E5E7EB"
                  onClick={() => setRejectReason((prev) => {
                    const next = prev ? `${prev}; ${tag}` : tag;
                    return next.slice(0, maxLen);
                  })}
                >
                  {tag}
                </Button>
              ))}
            </Flex>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Flex w="100%" gap={3} justify="flex-end" flexWrap="wrap">
              <Button ref={leastRef as any} variant="outline" onClick={onClose}>
                Cancel
              </Button>

              <Button
                bg="#4A2C2A"
                color="white"
                _hover={{ bg: "#4A2C2A" }}
                _disabled={{
                  bg: "#E2E2E2",
                  color: "#FFFFFF",
                  cursor: "not-allowed",
                  opacity: 0.7,
                }}
                isDisabled={isDisabled || overLimit}
                onClick={onConfirm}
              >
                {submitLabel}
              </Button>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default RejectReasonModal;
